import { withPersistTimeout } from "./persistTimeout";
import type { UploadTelemetry } from "./ports";
import type { UploadEndpointFamily } from "./targetInitiation";
import { malformedListPartsError, type ListPartsEvidence, type ReconciliationPart } from "./reconciliationStateMachine";
import { isConfirmedMigrationIntegrityError } from "./driverUrlRefresh";

/**
 * LSP-19201 — recovering the receipt for a multipart part S3 ACCEPTED but whose ETag we could not read.
 *
 * ## The failure this repairs
 *
 * A 2xx PUT whose `ETag` response header never reaches JavaScript is not a failed upload: the bytes
 * are on S3, only the receipt is missing. A TLS-inspecting proxy, VPN, antivirus shim or other
 * middlebox drops or renames the header per-request. Production (DataDog, 30 days) recorded 509
 * `S3 response missing ETag header` events from ONE device on ONE recording, spanning
 * 2026-08-21T19:07Z to 2026-08-27T15:23Z — six days — and every one of the 379 that carries a chunk
 * index names the SAME `chunkIndex: 3` (the remaining 130 are the outer `resumeFailed` records, which
 * carry none). Sibling parts on the identical origin, bucket and CORS configuration uploaded with
 * readable receipts, which is what rules out an `ExposeHeaders` misconfiguration and pins the loss to
 * the request path.
 *
 * Re-PUTting cannot fix it. The bytes were already accepted; the next attempt is accepted too, and
 * its receipt is stripped too. LSP-18742 correctly stopped that from hot-looping (`retry-slow`), but
 * pacing an unwinnable loop still leaves the guest at a frozen percentage indefinitely.
 *
 * ## Why the reconciliation adoption loop could not reach it
 *
 * `reconcilePersistedSession` already adopts server-side ETags — but only after a ListParts probe it
 * skips entirely when the local grid is intact:
 *
 *     if (missingBufferIndexes.length === 0 && missingMetadataCount === 0)
 *         return { kind: "ready", …, probedListParts: false };
 *
 * A stripped receipt leaves the buffer present and the metadata row present (just `etag: null`), so
 * both counters are zero, the probe never runs, and the adoption loop below it is unreachable. Its
 * implicit premise — "if I still hold the bytes, re-PUT them" — is exactly wrong when what was lost
 * is the receipt rather than the bytes. The information that distinguishes the two cases exists only
 * at the moment of failure, which is why the recovery is triggered from the chunk loop's exhausted
 * `MissingEtagError` rather than from resume-time state.
 *
 * ## Why this does not touch R-0
 *
 * R-0 (owner directive 2026-08-26, see `r0-no-attempt-count-terminal.guard.spec.ts`) forbids
 * deriving a TERMINAL state from an attempt count. Nothing here does: the PUT budget still bounds
 * the attempts, and exhausting it now asks the authoritative source for the receipt. A recovery
 * either succeeds — the upload proceeds — or the original `MissingEtagError` is rethrown verbatim and
 * LSP-18742's slow-tier pacing governs unchanged. No verdict gets worse, and no bytes are abandoned.
 *
 * ## The adoption bar
 *
 * The manifest is vetted by `malformedListPartsError` — the SAME exported bar
 * `reconcilePersistedSession` runs immediately before its own adoption loop — rather than by a local
 * re-implementation. That matters because `listPartsEvidenceFromResponse` casts `response.parts`
 * straight to `ReconciliationPart[]` without validating it, so a real server answer can carry a
 * duplicate/out-of-range/non-integer `partNumber`, a blank ETag, or a size of the wrong type. Break-it
 * round 1 adopted every one of those against a hand-rolled sub-bar; delegating removes both the gap
 * and any possibility of the two paths drifting on what "usable server evidence" means.
 *
 * On top of the shared bar this adds only the two facts the shared bar cannot know: the part must be
 * present at OUR part number, and — when the server reports a size — it must match the bytes we PUT.
 * Size parity is the only evidence available that the ETag covers OUR bytes; a part ETag is an MD5 and
 * `crypto.subtle` offers no MD5, so a cryptographic check is not reachable from the browser. It is
 * nonetheless a sound bar here for a reason reconciliation cannot rely on: the lease makes us the only
 * writer, and we hold a 2xx for these exact bytes at this exact part number from moments earlier.
 */
export type PartReceiptUnavailableReason =
	/** The object is already assembled; `classifyReconciliationTerminalEvidence` owns that verdict. */
	| "listing-assembled"
	/** The multipart upload is gone; the remote-gone recovery path owns that verdict. */
	| "listing-gone"
	/** The manifest failed the shared `malformedListPartsError` bar — it cannot be acted on at all. */
	| "listing-malformed"
	/** The server holds no part at this part number, so nothing was accepted after all. */
	| "part-absent"
	/** The server's part size contradicts the bytes we PUT, so its ETag does not cover them. */
	| "part-size-mismatch"
	/** The server reported no size for the part, so parity with our bytes cannot be proven at all. */
	| "part-size-unproven"
	/** The listed part's `/uploads` target identity disagrees with the envelope our session pinned. */
	| "part-target-mismatch"
	/** Our own expected size is unusable, so the size-parity check cannot be performed at all. */
	| "expected-size-unusable";

export type PartReceiptRecovery = { kind: "recovered"; etag: string } | { kind: "unavailable"; reason: PartReceiptUnavailableReason };

/**
 * Decides whether a server ListParts manifest can supply the receipt for one part, and says WHY when
 * it cannot. Pure so both the refusal reasons and the adoption bar are unit-testable without a
 * transport, and so the caller can log every refusal by name (`CLAUDE.md` → "Diagnostic logging when
 * fixing silent failures": a recovery that declines silently is the same invisible dead end this
 * ticket exists to remove).
 */
export function recoverPartReceiptFromListing(options: {
	listing: ListPartsEvidence;
	partNumber: number;
	expectedPartSize: number;
	totalChunks: number;
	/** The `/uploads` target our session pinned for this ordinal; `undefined` for the legacy family. */
	expectedTargetId?: string;
}): PartReceiptRecovery {
	const { listing, partNumber, expectedPartSize, totalChunks, expectedTargetId } = options;
	// FIRST, before any branch reads the listing: the shared bar rejects a manifest the client must not
	// act on, and it tolerates `unknown`, so it also catches a probe that resolved a non-object at all
	// (undefined / a number / a string — break-it round 1). Everything past this line is a manifest
	// whose parts have unique integer part numbers within 1..totalChunks, valid ETags, and sizes that
	// are either uniformly absent or positive safe integers.
	if (malformedListPartsError(listing, totalChunks)) return { kind: "unavailable", reason: "listing-malformed" };
	// `assembled` and `gone` both mean the multipart upload id is CONSUMED, and neither is this
	// function's verdict to act on. The architecture-gap review argued they should terminalize here,
	// on the grounds that intact local buffers stop reconciliation ever re-probing (the very gate this
	// ticket exists to route around) and so the session would re-PUT forever. It does not: the next
	// pass's PUT against a consumed upload id returns NoSuchUpload, and `uploadChunk` maps that to
	// MultipartUploadGoneError and rethrows it immediately, straight into the existing gone-recovery
	// route that already distinguishes "already assembled" from "genuinely gone" by attempting the
	// completion. The real cost is one extra pass, not stranding — so refusing here and letting the
	// established owner of that verdict reach it is both correct and the non-destructive choice.
	// Conflating the two from inside the chunk loop is what would risk re-uploading a delivered file.
	// Both are logged by name (`listing-assembled` / `listing-gone`) so the state stays queryable.
	if (listing.kind === "assembled") return { kind: "unavailable", reason: "listing-assembled" };
	if (listing.kind === "gone") return { kind: "unavailable", reason: "listing-gone" };
	// Checked before the manifest lookup: a NaN/negative/non-integer expected size would make the
	// size-parity comparison below silently vacuous (every `!==` against NaN is true, so a mismatch
	// would read as a mismatch for the wrong reason, and a `size`-less server part would be adopted on
	// a comparison that never ran). Refusing keeps the ONE fact this function stakes adoption on
	// honest — see PersistedByteIntegrityError for the sibling rule about unusable local sizes.
	if (!Number.isSafeInteger(expectedPartSize) || expectedPartSize <= 0) {
		return { kind: "unavailable", reason: "expected-size-unusable" };
	}
	const serverPart = listing.parts.find((part) => part.partNumber === partNumber);
	if (!serverPart) return { kind: "unavailable", reason: "part-absent" };
	// No local ETag re-check: the shared bar above already rejected the whole manifest if ANY part
	// carried an invalid one, so a second `isValidEtag` here could not fail. One source of truth.
	//
	// Size parity is REQUIRED, not merely "checked when reported". `ReconciliationPart.size` is typed
	// optional and its comment claims legacy `/multipart/:uploadId/list-parts` responses omit it, but
	// the server disagrees: `s3Service.listMultipartParts` returns `size` for every part and REFUSES
	// (MultipartPartsListingAnomalyError) any manifest whose part lacks PartNumber/ETag/Size — LSP-17796.
	// So an absent size is not the legacy norm the comment describes; it is an answer we cannot verify.
	// `reconciliationStateMachine`'s adoption loop tolerates it because it is choosing between adopting
	// and abandoning bytes that no longer exist locally. This path is not: our bytes are intact and the
	// fallback is a paced retry, so refusing costs nothing and never adopts an ETag we cannot tie to the
	// bytes we PUT (break-it round 1, D5).
	if (typeof serverPart.size !== "number") return { kind: "unavailable", reason: "part-size-unproven" };
	if (serverPart.size !== expectedPartSize) {
		return { kind: "unavailable", reason: "part-size-mismatch" };
	}
	// `/uploads` target identity (break-it round 4). Completion pairs the ETag with the targetId our
	// SESSION pinned for this ordinal, not the one the listing reported (see asyncChunkedUploadDriver's
	// complete()), so adopting across a CONTRADICTION would post a mismatched pair. The server catches
	// that itself — `assertMultipartPartTargets` throws UploadNotFoundError — and because it derives
	// `targetId` as `attemptId:ordinal` from the attempt we asked about, a server-produced contradiction
	// is not actually reachable. Checking anyway is a pure refusal that costs a paced retry instead of a
	// server-side 404, and it closes the one dimension of the identity contract this bar did not cover.
	//
	// Scoped to a row that ASSERTS a conflicting identity, not to one that asserts none. Round 4's own
	// control caught the over-strict version: since completion uses OUR pinned targetId either way, a
	// silent row cannot produce a mispairing, so refusing it would only strand recoverable receipts —
	// including every legacy-family row, which carries no targetId at all.
	if (serverPart.targetId !== undefined && serverPart.targetId !== expectedTargetId) {
		return { kind: "unavailable", reason: "part-target-mismatch" };
	}
	return { kind: "recovered", etag: serverPart.etag };
}

/**
 * The chunk loop's recovery port: one ListParts probe for one part, answering with an adoptable ETag
 * or `null`. Built by `createPartReceiptRecovery` so contact-async and contact-admin cannot drift on
 * the probe, the adoption bar, or the telemetry.
 */
export type PartReceiptRecoveryPort = (input: {
	chunkIndex: number;
	partNumber: number;
	expectedPartSize: number;
	totalChunks: number;
	expectedTargetId?: string;
	error: Error;
}) => Promise<string | null>;

/**
 * The `/uploads` target identity our session pinned for one ordinal, or `undefined` for a legacy
 * session (which carries no envelopes and whose listings carry no `targetId`).
 *
 * A malformed/absent envelope answers `undefined`, which the adoption bar then reads as a
 * disagreement with any listing row that DOES carry a targetId — the fail-safe direction: refuse and
 * fall back to the paced retry rather than adopt an identity we could not pin.
 */
export function pinnedUploadsTargetIdForPart(
	session: { endpointFamily?: UploadEndpointFamily; targetEnvelopesJson?: string },
	partNumber: number
): string | undefined {
	if (session.endpointFamily !== "uploads") return undefined;
	let envelopes: unknown;
	try {
		envelopes = JSON.parse(session.targetEnvelopesJson ?? "");
	} catch {
		return undefined;
	}
	if (!Array.isArray(envelopes)) return undefined;
	const envelope = envelopes.find(
		(candidate) => !!candidate && typeof candidate === "object" && (candidate as { ordinal?: unknown }).ordinal === partNumber
	) as { targetId?: unknown } | undefined;
	return typeof envelope?.targetId === "string" && envelope.targetId.trim().length > 0 ? envelope.targetId : undefined;
}

/**
 * Wires the pure adoption bar to a ListParts probe, logging every outcome.
 *
 * Every exit is logged, success included. `mocked-delivery-hides-broken-transport` is the sibling
 * lesson: a repair path that only logs its failures is indistinguishable from a repair path that
 * never ran, and this one runs on the rare request nobody is watching.
 *
 * One probe per exhausted chunk, deliberately not memoized across the pass. A cached listing fetched
 * before a later part was PUT would answer `part-absent` for a part the server does hold, turning a
 * recoverable receipt into an unrecoverable one — the exact staleness trap
 * `diagnostic-snapshot-vs-live-copy` describes. The probe count is already bounded by the pending
 * chunk count and gated behind an exhausted retry budget, and each probe that answers recovers a
 * part rather than merely observing it.
 */
/**
 * How long one ListParts probe may take before the recovery gives up on it.
 *
 * Break-it round 1 (D13): without a deadline, a probe that never settles wedges the chunk worker, and
 * with it the whole upload pass — strictly worse than the pre-change behaviour, where an exhausted
 * MissingEtagError threw immediately and the drain moved on. A pass counter cannot bound a hung
 * promise; only a raced deadline can. Thirty seconds is far beyond any healthy list-parts round trip
 * while staying well inside the slow tier's own sixty-second base delay, so a timed-out probe never
 * delays the retry that follows it.
 */
export const PART_RECEIPT_PROBE_TIMEOUT_MS = 30_000;

/** Part count for the logs, `undefined` for any answer that is not a verified manifest. */
function describeListPartsCount(listing: unknown): number | undefined {
	if (typeof listing !== "object" || listing === null) return undefined;
	const candidate = listing as { kind?: unknown; parts?: unknown };
	return candidate.kind === "verified" && Array.isArray(candidate.parts) ? candidate.parts.length : undefined;
}

export function createPartReceiptRecovery(options: {
	partReceipt?: (ordinal: number) => Promise<ReconciliationPart | null>;
	/**
	 * Read lazily rather than bound at wiring time: `chunkedUploadDriver` reassigns its `session` when
	 * it ensures an upload target, so a probe holding the earlier value could list the wrong multipart.
	 */
	listParts: () => Promise<ListPartsEvidence>;
	telemetry: Pick<UploadTelemetry, "logInfo" | "logWarn">;
	context: () => Record<string, unknown>;
	/** Overridable so tests can assert the bound without waiting for it. */
	probeTimeoutMs?: number;
}): PartReceiptRecoveryPort {
	/**
	 * Break-it round 2 (D2): the port's declared answer is `string | null`, and that must be true of the
	 * port ITSELF rather than true only because `driverChunkLoop` catches for it. Round 2 reached this by
	 * handing `malformedListPartsError` a Proxy whose `kind` / `parts.length` getter throws — not
	 * something `JSON.parse` can produce, so this is defence in depth rather than a live defect, but a
	 * throw escaping here would reach the app's classifyFailure as a bare Error and be classified
	 * "other" (release to pending AND rethrow) instead of MissingEtagError's slow tier. A repair path
	 * must never hand back a WORSE verdict than the failure it was repairing.
	 */
	// Break-it round 6 (D1): every telemetry call in this module is made inert on failure.
	//
	// This is the invariant, not a nicety: round 2's fail-closed wrapper turned an ALREADY RECOVERED,
	// size-matched, identity-matched ETag into `null` when the SUCCESS log threw — observability
	// destroying the very repair it was reporting, leaving a file on slow retries the server had
	// everything needed to finish. A guard that can discard a good receipt is worse than the throw it
	// was catching. Making the logs unable to throw fixes it at the source, so the wrapper below is left
	// to catch only genuine decision errors.
	// Rest-forwarded, not re-listed as fixed parameters: naming `(event, context, thrown)` here would
	// hand every call a third `undefined` argument, changing the arity the real telemetry port observes.
	const safeTelemetry: Pick<UploadTelemetry, "logInfo" | "logWarn"> = {
		logInfo: (...args: Parameters<UploadTelemetry["logInfo"]>) => {
			try {
				options.telemetry.logInfo(...args);
			} catch {
				// Never let a broken logger change the upload's outcome.
			}
		},
		logWarn: (...args: Parameters<UploadTelemetry["logWarn"]>) => {
			try {
				options.telemetry.logWarn(...args);
			} catch {
				// Never let a broken logger change the upload's outcome.
			}
		}
	};

	/** Both call sites always carry a cause, so the arity is fixed at three. */
	const answerNull = (event: string, context: Record<string, unknown>, thrown: unknown): null => {
		safeTelemetry.logWarn(event, context, thrown);
		return null;
	};

	return async (input) => {
		const { chunkIndex, partNumber, expectedPartSize, totalChunks, expectedTargetId, error } = input;
		// A throwing `context()` is deliberately NOT caught here. Break-it round 2 pins that it must reach
		// driverChunkLoop's catch, which logs it and degrades to the same paced retry — swallowing it here
		// and repairing against a degraded log context was extra machinery that broke that guarantee for
		// no customer benefit, since neither in-repo builder can throw unless the session is already gone.
		const context = { ...options.context(), chunkIndex, partNumber, expectedPartSize };
		let listing: ListPartsEvidence;
		try {
			// `Promise.resolve` first: the port is typed to return a promise, but an out-of-tree caller can
			// hand back a bare value, and withPersistTimeout needs a real thenable to race the deadline
			// against. A rejection from either side lands in the same catch below.
			listing = await withPersistTimeout(
				options.partReceipt
					? Promise.resolve(options.partReceipt(partNumber)).then(
							(part): ListPartsEvidence => ({ kind: "verified", parts: part ? [part] : [] })
						)
					: Promise.resolve(options.listParts()),
				options.probeTimeoutMs ?? PART_RECEIPT_PROBE_TIMEOUT_MS,
				"Multipart part receipt recovery"
			);
		} catch (probeError) {
			if (isConfirmedMigrationIntegrityError(probeError)) throw probeError;
			// Includes AmbiguousListPartsError, which the port throws for a manifest the server could not
			// vouch for, and PersistTimeoutError from the deadline above. Answering `null` keeps the
			// caller's original MissingEtagError verdict, which is the non-destructive one — never a worse
			// verdict derived from an unresolved listing.
			return answerNull(
				"recording.fileUpload.partReceiptRecoveryProbeFailed",
				{ ...context, missingEtagError: error.message },
				probeError
			);
		}
		try {
			return decideAndReport(safeTelemetry, listing, { partNumber, expectedPartSize, totalChunks, expectedTargetId }, context, error);
		} catch (decisionError) {
			return answerNull("recording.fileUpload.partReceiptRecoveryThrew", context, decisionError);
		}
	};
}

/**
 * The adoption decision plus its report. Extracted so the factory's fail-closed wrapper stays legible.
 *
 * `telemetry` here is always the factory's inert wrapper, never the raw port — see the round-6 note on
 * `safeTelemetry`. A throwing logger must not be able to reach this function's caller, because the
 * caller's only way to report a throw is to answer `null` and discard the receipt.
 */
function decideAndReport(
	telemetry: Pick<UploadTelemetry, "logInfo" | "logWarn">,
	listing: ListPartsEvidence,
	part: { partNumber: number; expectedPartSize: number; totalChunks: number; expectedTargetId?: string },
	context: Record<string, unknown>,
	error: Error
): string | null {
	const { partNumber, expectedPartSize, totalChunks, expectedTargetId } = part;
	const recovery = recoverPartReceiptFromListing({
		listing,
		partNumber,
		expectedPartSize,
		totalChunks,
		expectedTargetId
	});
	// Not `listing.kind`. Break-it round 1 (D10): a probe that resolved `undefined` was correctly
	// REFUSED by the malformed bar and then crashed this very log line with a TypeError, so the one
	// report that would have explained the refusal was the thing that broke. Diagnostics must survive
	// every input they exist to describe, including the malformed ones.
	const listPartsKind = typeof listing === "object" && listing !== null ? listing.kind : typeof listing;
	const listPartsCount = describeListPartsCount(listing);
	if (recovery.kind === "unavailable") {
		telemetry.logWarn("recording.fileUpload.partReceiptUnrecoverable", {
			...context,
			reason: recovery.reason,
			listPartsKind,
			...(listPartsCount === undefined ? {} : { listPartsCount }),
			missingEtagError: error.message
		});
		return null;
	}
	telemetry.logInfo("recording.fileUpload.partReceiptRecovered", { ...context, listPartsCount });
	return recovery.etag;
}
